import React from 'react';
import { RiFileList3Line, RiInboxArchiveLine, RiHandHeartLine } from 'react-icons/ri';
import { RelatedRecordsCard } from '../detail';
import itemRentalHistoryService, {
  ItemRentalHistory,
  ItemRentalHistoryRow,
  itemRentalHistoryKey,
  ITEM_RENTAL_HISTORY_LIMIT,
} from '../../services/itemRentalHistoryService';
import equipmentReturnItemService, { ReturnItem } from '../../services/equipmentReturnItemService';
import stockIssueService, { StockIssueRow, OPEN_ISSUE_STATUSES } from '../../services/stockIssueService';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';
import { formatDate } from '../../utils/dateUtils';
import { returnRequestUrl } from '../../utils/returnCancellationLinks';
import { cn } from '../../utils/cn';

/**
 * การ์ด "ข้อมูลที่เกี่ยวข้อง" ที่ใช้ร่วมกันระหว่างหน้ารายละเอียดอุปกรณ์และซิม (28 ต.ค. 2026)
 * แต่ละการ์ด gate สิทธิ์ดูของปลายทางเอง (RelatedRecordsCard: ไม่มีสิทธิ์ = ไม่ render + ไม่ยิง API)
 */
export type ItemKind = 'device' | 'sim';

const RELATED_LIMIT = 10;

interface ItemCardProps {
  kind: ItemKind;
  itemId: number;
  /** state ที่ส่งไปกับลิงก์แถว (กลับมาหน้านี้ได้) */
  rowState?: unknown;
}

/**
 * สัญญาเช่าที่ของชิ้นนี้เคยอยู่ / อยู่ตอนนี้ — "อยู่ตอนนี้" มาจาก backend (find_rental_holder) ไม่ใช่จาก status
 */
/** ดึงประวัติสัญญา (ใช้ร่วมกับส่วน "การถือครองตอนนี้" ของหน้า — queryKey เดียวกัน) */
export const fetchItemRentalHistory = (kind: ItemKind, itemId: number): Promise<ItemRentalHistory> =>
  kind === 'device'
    ? itemRentalHistoryService.getDeviceRentalHistory(itemId, ITEM_RENTAL_HISTORY_LIMIT)
    : itemRentalHistoryService.getSimRentalHistory(itemId, ITEM_RENTAL_HISTORY_LIMIT);

/** ป้าย "อยู่ตอนนี้" ของแถว — ของนับจำนวนบอกจำนวนแทน (อยู่หลายสัญญาพร้อมกันได้) */
export const currentRentalLabel = (r: ItemRentalHistoryRow, counted?: boolean): string =>
  counted
    ? `มีของอยู่ในสัญญานี้${r.quantity ? ` ${r.quantity} หน่วย` : ''}`
    : r.holds_inventory ? 'อยู่ตอนนี้' : 'ผูกกับสัญญานี้ (ร่าง)';

export const ItemRentalHistoryCard: React.FC<ItemCardProps & { counted?: boolean }> = ({ kind, itemId, rowState, counted }) => (
  <RelatedRecordsCard<ItemRentalHistoryRow>
    title="สัญญาเช่าที่เคยอยู่ / อยู่ตอนนี้"
    icon={<RiFileList3Line />}
    permission="rentals.view"
    queryKey={itemRentalHistoryKey(kind, itemId)}
    fetcher={() => fetchItemRentalHistory(kind, itemId)}
    rowKey={(r) => r.rental_item_id}
    rowTo={(r) => `/rentals/view/${r.rental_id}`}
    rowState={rowState}
    emptyText={kind === 'device' ? 'เครื่องนี้ยังไม่เคยอยู่ในสัญญาเช่า' : 'ซิมนี้ยังไม่เคยอยู่ในสัญญาเช่า'}
    testId={`${kind}-related-rentals`}
    renderRow={(r) => {
      const st = getRentalStatusDisplay(r.status);
      return (
        <>
          <div className="min-w-0">
            <p className="text-sm font-medium text-secondary-900 font-mono flex flex-wrap items-center gap-2">
              {r.rental_number || `#${r.rental_id}`}
              {r.is_current && (
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-sans font-semibold bg-primary-100 text-primary-800"
                  data-testid="rental-history-current"
                >
                  {currentRentalLabel(r, counted)}
                </span>
              )}
            </p>
            <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
              {[r.company_name, r.site_name].filter(Boolean).join(' · ') || '-'}
            </p>
            <p className="text-xs text-secondary-400">
              เริ่ม {formatDate(r.rental_date ?? undefined)}
              {r.billing_end_date ? ` · เก็บเงินถึง ${formatDate(r.billing_end_date ?? undefined)}` : ''}
            </p>
          </div>
          <span className={cn('px-2 py-0.5 rounded-full text-xs flex-shrink-0', st.badgeColor)}>{st.label}</span>
        </>
      );
    }}
  />
);

/** ใบตามเก็บอุปกรณ์ของชิ้นนี้ (1 แถว = ของ 1 ชิ้น · กรอง device_id / sim_id ที่ backend) */
export const ItemReturnItemsCard: React.FC<ItemCardProps> = ({ kind, itemId }) => (
  <RelatedRecordsCard<ReturnItem>
    title="ใบตามเก็บอุปกรณ์"
    icon={<RiInboxArchiveLine />}
    permission="equipment_returns.view"
    queryKey={['item-related', kind, itemId, 'return-items']}
    fetcher={() =>
      equipmentReturnItemService.getReturnItems(
        kind === 'device' ? { device_id: itemId, limit: RELATED_LIMIT } : { sim_id: itemId, limit: RELATED_LIMIT }
      )}
    rowKey={(r) => r.id}
    // หน้าใบตามเก็บเต็ม (767233c4) — เปิดใบของชิ้นนั้นพร้อมไฮไลต์ ?item=
    rowTo={(r) => returnRequestUrl(r.return_request_id, r.id)}
    viewAllTo="/rentals/equipment-returns"
    emptyText="ไม่มีรายการตามเก็บ"
    testId={`${kind}-related-returns`}
    renderRow={(r) => {
      const open = !['COMPLETED', 'CANCELLED', 'LOST'].includes(String(r.status));
      return (
        <>
          <div className="min-w-0">
            <p className="text-sm font-medium text-secondary-900 font-mono">
              {r.return_request?.rental?.rental_number || (r.return_request?.rental_id ? `#${r.return_request.rental_id}` : `รายการ #${r.id}`)}
            </p>
            <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
              {[r.return_request?.rental?.company?.name, r.return_request?.rental?.site?.name].filter(Boolean).join(' · ') || '-'}
            </p>
            <p className="text-xs text-secondary-400">
              สร้าง {formatDate(r.created_at)}
              {r.actual_return_date ? ` · รับคืน ${formatDate(r.actual_return_date)}` : ''}
            </p>
          </div>
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-xs flex-shrink-0',
              open ? 'bg-warning-100 text-warning-800' : 'bg-secondary-100 text-secondary-700'
            )}
          >
            {equipmentReturnItemService.getStatusLabel(r.status)}
          </span>
        </>
      );
    }}
  />
);

/** ใบเบิก-ยืมที่มีของชิ้นนี้ (ใครยืมไป · คืนแล้วหรือยัง) */
export const ItemStockIssuesCard: React.FC<ItemCardProps> = ({ kind, itemId, rowState }) => (
  <RelatedRecordsCard<StockIssueRow>
    title="ใบเบิก-ยืมอุปกรณ์"
    icon={<RiHandHeartLine />}
    permission="stock_issues.view"
    queryKey={['item-related', kind, itemId, 'stock-issues']}
    fetcher={() =>
      stockIssueService.getIssues(
        kind === 'device' ? { device_id: itemId, page_size: RELATED_LIMIT } : { sim_id: itemId, page_size: RELATED_LIMIT }
      )}
    rowKey={(r) => r.id}
    // ใบที่ยังเปิดมีหน้าติดตามคืนรายใบ · ใบที่ปิดแล้วไม่มีหน้ารายตัว → ไม่ทำลิงก์
    rowTo={(r) => (OPEN_ISSUE_STATUSES.includes(r.status as any) ? `/stock/pending-returns?issue=${r.id}` : null)}
    rowState={rowState}
    viewAllTo="/stock/issues?tab=history"
    emptyText="ไม่เคยถูกเบิก/ยืม"
    testId={`${kind}-related-stock-issues`}
    renderRow={(r) => (
      <>
        <div className="min-w-0">
          <p className="text-sm font-medium text-secondary-900 font-mono">{r.issue_number}</p>
          <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
            {r.purpose_label || '-'}
            {r.issued_to_display ? ` · ${r.issued_to_display}` : ''}
          </p>
          <p className="text-xs text-secondary-400">เบิก {formatDate(r.issue_date ?? undefined)}</p>
        </div>
        <span className="text-xs text-secondary-700 flex-shrink-0">{r.status_label || r.status}</span>
      </>
    )}
  />
);
